<template>
  <div id="tosf-hei-summary">
    <div class="row">
      <div class="col">
        <!-- HEI Information Summary -->
        <div class="card mb-4">
          <div class="card-header bg-success text-white">
            <h6 class="mb-0">
              <i class="bi bi-info-circle mr-2"></i>HEI Information Summary
            </h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <table class="table table-sm table-borderless">
                  <tbody>
                    <tr>
                      <td class="font-weight-bold">HEI Code:</td>
                      <td>{{ heiProfile.hei_code }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">HEI Name:</td>
                      <td>{{ heiProfile.hei_name }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Academic Year:</td>
                      <td>{{ heiProfile.academic_year }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Status:</td>
                      <td>
                        <span 
                          class="badge"
                          :class="{
                            'badge-success': heiProfile.hei_status === 'Active',
                            'badge-warning': heiProfile.hei_status === 'Inactive',
                            'badge-danger': heiProfile.hei_status === 'Suspended'
                          }"
                        >
                          {{ heiProfile.hei_status }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="col-md-6">
                <table class="table table-sm table-borderless">
                  <tbody>
                    <tr>
                      <td class="font-weight-bold">Reference Number:</td>
                      <td>{{ heiProfile.reference_number || 'N/A' }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Address:</td>
                      <td>{{ heiProfile.hei_address }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Created At:</td>
                      <td>{{ heiProfile.created_at | standardDate }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold">Updated At:</td>
                      <td>{{ heiProfile.updated_at | standardDate }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Financial Summary -->
        <div class="card mb-4">
          <div class="card-header bg-warning text-dark">
            <h6 class="mb-0">
              <i class="bi bi-calculator mr-2"></i>Financial Summary
            </h6>
          </div>
          <div class="card-body">
            <div v-if="heiProfile.programs_data && heiProfile.programs_data.length > 0">
              <div class="row">
                <div class="col-md-3">
                  <div class="text-center">
                    <h4 class="text-primary">{{ heiProfile.programs_data.length }}</h4>
                    <p class="text-muted mb-0">Total Programs</p>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="text-center">
                    <h4 class="text-info">{{ calculateTotalSemesters() }}</h4>
                    <p class="text-muted mb-0">Total Semesters</p>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="text-center">
                    <h4 class="text-success">₱{{ calculateAverageFees().toLocaleString() }}</h4>
                    <p class="text-muted mb-0">Average Fees</p>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="text-center">
                    <h4 class="text-warning">₱{{ calculateTotalFees().toLocaleString() }}</h4>
                    <p class="text-muted mb-0">Grand Total</p>
                  </div>
                </div>
              </div>

              <!-- Detailed Breakdown -->
              <div class="mt-4">
                <h6>Program Breakdown:</h6>
                <div class="table-responsive">
                  <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                      <tr>
                        <th>Program</th>
                        <th class="text-center">Years</th>
                        <th class="text-center">Semesters</th>
                        <th class="text-right">Total Fees</th>
                        <th class="text-right">Average per Semester</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(program, index) in heiProfile.programs_data" :key="index">
                        <td class="font-weight-bold">{{ program.degree_program || `Program ${index + 1}` }}</td>
                        <td class="text-center">{{ program.years ? program.years.length : 0 }}</td>
                        <td class="text-center">{{ calculateProgramSemesters(program) }}</td>
                        <td class="text-right font-weight-bold text-success">
                          ₱{{ calculateProgramTotal(program).toLocaleString() }}
                        </td>
                        <td class="text-right">
                          ₱{{ calculateProgramAverage(program).toLocaleString() }}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="table-info">
                        <td colspan="3" class="text-right font-weight-bold">Grand Total:</td>
                        <td class="text-right font-weight-bold text-primary">
                          ₱{{ calculateTotalFees().toLocaleString() }}
                        </td>
                        <td class="text-right font-weight-bold">
                          ₱{{ calculateOverallAverage().toLocaleString() }}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted">
              <i class="bi bi-inbox display-4"></i>
              <p class="mt-2">No programs available</p>
            </div>
          </div>
        </div>

        <!-- Export Options -->
        <div class="card">
          <div class="card-header bg-secondary text-white">
            <h6 class="mb-0">
              <i class="bi bi-download mr-2"></i>Export Options
            </h6>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <button class="btn btn-outline-primary btn-block" @click="exportProfile">
                  <i class="bi bi-file-earmark-text mr-2"></i>Export Profile Data
                </button>
              </div>
              <div class="col-md-6">
                <button class="btn btn-outline-success btn-block" @click="exportPrograms">
                  <i class="bi bi-file-earmark-spreadsheet mr-2"></i>Export Programs Data
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TosfHeiSummary',
  props: ['heiProfile'],
  methods: {
    calculateProgramTotal(program) {
      if (!program.years) return 0;
      
      let total = 0;
      program.years.forEach(year => {
        const firstSemTotal = parseFloat((year.first_sem && year.first_sem.total_fees) || 0);
        const secondSemTotal = parseFloat((year.second_sem && year.second_sem.total_fees) || 0);
        total += firstSemTotal + secondSemTotal;
      });
      
      return total;
    },

    calculateProgramSemesters(program) {
      if (!program.years) return 0;
      return program.years.length * 2; // Each year has 2 semesters
    },

    calculateProgramAverage(program) {
      const total = this.calculateProgramTotal(program);
      const semesters = this.calculateProgramSemesters(program);
      return semesters > 0 ? Math.round(total / semesters) : 0;
    },

    calculateTotalFees() {
      if (!this.heiProfile.programs_data) return 0;
      
      let grandTotal = 0;
      this.heiProfile.programs_data.forEach(program => {
        grandTotal += this.calculateProgramTotal(program);
      });
      
      return grandTotal;
    },

    calculateTotalSemesters() {
      if (!this.heiProfile.programs_data) return 0;
      
      let totalSemesters = 0;
      this.heiProfile.programs_data.forEach(program => {
        totalSemesters += this.calculateProgramSemesters(program);
      });
      
      return totalSemesters;
    },

    calculateAverageFees() {
      if (!this.heiProfile.programs_data || this.heiProfile.programs_data.length === 0) return 0;
      
      const totalFees = this.calculateTotalFees();
      return Math.round(totalFees / this.heiProfile.programs_data.length);
    },

    calculateOverallAverage() {
      const totalFees = this.calculateTotalFees();
      const totalSemesters = this.calculateTotalSemesters();
      return totalSemesters > 0 ? Math.round(totalFees / totalSemesters) : 0;
    },

    exportProfile() {
      // Create a downloadable JSON file with profile data
      const dataStr = JSON.stringify(this.heiProfile, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `hei-profile-${this.heiProfile.hei_code}-${this.heiProfile.academic_year}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      toastr.success('Profile data exported successfully');
    },

    exportPrograms() {
      // Create a downloadable CSV file with programs data
      if (!this.heiProfile.programs_data || this.heiProfile.programs_data.length === 0) {
        toastr.warning('No programs data to export');
        return;
      }

      let csvContent = 'Program,Year,Semester,Units,Tuition Per Unit,Other Fees,Total Fees\n';
      
      this.heiProfile.programs_data.forEach((program, programIndex) => {
        const programName = program.degree_program || `Program ${programIndex + 1}`;
        
        if (program.years) {
          program.years.forEach((year, yearIndex) => {
            // First semester
            csvContent += `${programName},Year ${yearIndex + 1},1st Sem,${year.first_sem.units || 0},${year.first_sem.tuition_per_unit || 0},${year.first_sem.other_fees || 0},${year.first_sem.total_fees || 0}\n`;
            // Second semester
            csvContent += `${programName},Year ${yearIndex + 1},2nd Sem,${year.second_sem.units || 0},${year.second_sem.tuition_per_unit || 0},${year.second_sem.other_fees || 0},${year.second_sem.total_fees || 0}\n`;
          });
        }
      });
      
      const dataUri = 'data:text/csv;charset=utf-8,'+ encodeURIComponent(csvContent);
      const exportFileDefaultName = `hei-programs-${this.heiProfile.hei_code}-${this.heiProfile.academic_year}.csv`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      toastr.success('Programs data exported successfully');
    }
  }
};
</script>

<style scoped>
.table-borderless td {
  border: none;
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-info {
  background-color: #d1ecf1;
}

.btn-block {
  width: 100%;
}
</style>
